/**
 * Canonical confidence thresholds for field-value OCR output.
 *
 * Used by `canvas/renderer.ts` (bbox overlay colors), `FieldInput.tsx`
 * (per-field border colors), and `BboxLegend.tsx` (legend swatches).
 * Keep all three sites in sync via this module — do not hard-code.
 */
export const CONFIDENCE_HIGH = 0.85;
export const CONFIDENCE_MEDIUM = 0.65;
export function tierOf(confidence) {
    if (confidence === null || confidence === undefined)
        return "none";
    if (confidence >= CONFIDENCE_HIGH)
        return "high";
    if (confidence >= CONFIDENCE_MEDIUM)
        return "medium";
    return "low";
}
/**
 * R-32 Phase 2 — one per-Job-Type (optionally per-field) verify threshold
 * drives BOTH tab-skip and the 3-state highlight. See
 * docs/plans/2026-05-16-r32-phase2-design.md.
 */
export const DEFAULT_VERIFY_THRESHOLD = 0.95;
/**
 * Effective threshold for a field: per-field override → per-JobType →
 * default. `??` only coalesces null/undefined, so 0 (a valid "skip
 * everything" threshold) and 1 (always-verify) pass through unchanged.
 */
export function resolveEffectiveThreshold(fieldThreshold, jobTypeThreshold) {
    return fieldThreshold ?? jobTypeThreshold ?? DEFAULT_VERIFY_THRESHOLD;
}
/**
 * R-128 / Phase 1.5 WU-3 — per-field "confidence floor" with the per-data-type
 * cascade. Used by the verification station's smart-skip iterator (R-127) and
 * the "needs attention" counter (R-135) to decide whether a field qualifies as
 * low-confidence — i.e. whether the cursor should land on it during keying.
 *
 * Precedence:
 *   field.verifyThreshold (per-field override)   wins when non-null
 *   ↓ otherwise
 *   typeFloors[field.dataType]                    per-data-type from
 *                                                 JobType.verificationHighlightThresholds
 *   ↓ otherwise
 *   DEFAULT_VERIFY_THRESHOLD                      0.95 platform default
 *
 * Semantics: a field at 1.00 always requires keying (no OCR confidence can
 * meet "≥ 1.00 with strict >"); a field at 0 never stops (always tab-skipped).
 *
 * Implementation note: this re-uses the existing `verifyThreshold` column
 * rather than adding a parallel `confidenceFloor`. The two concepts are
 * identical — R-128 just gives the existing column the operationally-correct
 * name. The plan's `3.1 Prisma column` step is INTENTIONALLY SKIPPED;
 * `verifyThreshold` is the source of truth.
 */
export function effectiveFloor(field, typeFloors) {
    if (field.verifyThreshold != null)
        return field.verifyThreshold;
    const typeFloor = typeFloors?.[field.dataType];
    if (typeFloor != null)
        return typeFloor;
    return DEFAULT_VERIFY_THRESHOLD;
}
/**
 * 3-state highlight. Precedence:
 *   failed rule           → red    (bad even at high confidence)
 *   rule-pass/override    → blank  (resolved)
 *   operator-edited       → blank  (resolved)
 *   operator-acknowledged → blank  (R-144 — Tab/Enter accepted as-is)
 *   confidence null       → red    (not recognized)
 *   conf ≥ threshold      → blank  (skipped)
 *   else (recognized,     → yellow (verify)
 *         below thresh)
 */
export function resolveFieldHighlight(field, threshold) {
    if (field.validationStatus === "fail")
        return "red";
    if (field.validationStatus === "pass" || field.validationStatus === "overridden")
        return "blank";
    if (field.isDirty)
        return "blank";
    if (field.isAcknowledged)
        return "blank";
    if (field.ocrConfidence === null)
        return "red";
    if (field.ocrConfidence >= threshold)
        return "blank";
    return "yellow";
}
//# sourceMappingURL=confidence-thresholds.js.map